import { useEffect, useState } from "react";

import type { Deviation, DeviationsResponse, Journey, JourneysResponse } from "../contract/types";
import { useBackend } from "../app/BackendProvider";
import { useStreamContext } from "../app/StreamProvider";
import { useLiveCollection } from "../app/useLiveCollection";
import { useNetworkId } from "../config/ConfigProvider";

/** File d'écarts en direct : instantané `/deviations` + deltas du sujet `deviations`. */
export function useDeviations(postParam: string | undefined) {
  const backend = useBackend();
  const networkId = useNetworkId();
  const { stream } = useStreamContext();

  return useLiveCollection<Deviation, DeviationsResponse>({
    topic: "deviations",
    stream,
    loadSnapshot: (signal) =>
      backend.api.getDeviations(networkId, postParam ? { post: postParam } : {}, signal),
    selectItems: (snapshot) => snapshot.deviations,
    selectSeq: (snapshot) => snapshot.seq,
    keyOf: (deviation) => deviation.id,
    deps: [backend, networkId, postParam],
  });
}

/** Courses actives en direct : instantané `/journeys` + deltas du sujet `journeys`. */
export function useLiveJourneys(postParam: string | undefined) {
  const backend = useBackend();
  const networkId = useNetworkId();
  const { stream } = useStreamContext();

  return useLiveCollection<Journey, JourneysResponse>({
    topic: "journeys",
    stream,
    loadSnapshot: (signal) =>
      backend.api.getJourneys(networkId, postParam ? { post: postParam } : {}, signal),
    selectItems: (snapshot) => snapshot.journeys,
    selectSeq: (snapshot) => snapshot.seq,
    keyOf: (journey) => journey.id,
    deps: [backend, networkId, postParam],
    // Position GPS : re-`GET` toutes les 12 s même si le flux paraît vivant —
    // borne la latence d'affichage quand un delta se fait attendre.
    refreshIntervalMs: 12_000,
  });
}

/**
 * Rafraîchit une ressource REST périodiquement (KPI, synoptique, intervalles…)
 * en plus d'un rechargement sur changement de dépendances.
 */
export function usePolledResource<T>(
  fetcher: (signal: AbortSignal) => Promise<T>,
  deps: ReadonlyArray<unknown>,
  intervalMs = 20_000,
): { data: T | undefined; error: unknown; loading: boolean } {
  const [state, setState] = useState<{ data: T | undefined; error: unknown; loading: boolean }>({
    data: undefined,
    error: undefined,
    loading: true,
  });

  useEffect(() => {
    let alive = true;
    let controller = new AbortController();

    const run = () => {
      controller = new AbortController();
      fetcher(controller.signal).then(
        (data) => alive && setState({ data, error: undefined, loading: false }),
        (error) => {
          if (!alive || (error instanceof DOMException && error.name === "AbortError")) return;
          setState((s) => ({ ...s, error, loading: false }));
        },
      );
    };

    run();
    const timer = setInterval(run, intervalMs);
    return () => {
      alive = false;
      controller.abort();
      clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, intervalMs]);

  return state;
}
